/**
 * Moz V3 API Helpers (Story 20.7, AC #2, AC #4)
 *
 * Per-method REST-shaped helpers wrapping `mozJsonRpcCall` from `json-rpc.ts`.
 * Each helper:
 *   - Validates required parameters synchronously (throws `TypeError` on
 *     missing `scope` or `limit > 50` per AC #4 — silent fallback would mask
 *     widget-config bugs).
 *   - Builds the JSON-RPC `params.data` payload with the exact shape the
 *     Moz V3 method expects.
 *   - Returns a uniform `MozHelperResult<TRecord>` shape: `{ records, rowsCharged }`.
 *
 * **rowsCharged accounting:** Moz charges per-row for list-style methods.
 * The helper returns the row count as `rowsCharged` so the adapter can wire
 * the per-tenant row-budget circuit-breaker + per-call telemetry.
 *
 * Source: moz.com/api/docs verified 2026-05-10.
 */

import type { AdapterEnv } from "../adapter";
import { mozJsonRpcCall } from "./json-rpc";
import { MOZ_LIST_LIMIT_MAX } from "./row-budget";
import {
  transformMozAnchorTextRow,
  transformMozBrandAuthorityRow,
  transformMozLinkingRootDomainsRow,
  transformMozSiteMetricsRow,
  transformMozTopPagesRow,
  type MozAnchorTextApiRow,
  type MozBrandAuthorityApiRow,
  type MozLinkingRootDomainsApiRow,
  type MozSiteMetricsApiRow,
  type MozTopPagesApiRow,
} from "./transformers";
import { isMozScope, type MozScope } from "./normalize";

// ============================================================================
// Common types
// ============================================================================

export interface MozHelperResult<TRecord> {
  readonly records: ReadonlyArray<TRecord>;
  readonly rowsCharged: number;
}

export interface MozQuotaLookupResult {
  readonly rowsRemaining: number;
  readonly rowsLimit: number;
  readonly rowsUsed: number;
}

export interface MozValidationResult {
  readonly rowsRemaining: number;
  readonly rowsLimit: number;
  readonly brandAuthorityEntitled: boolean;
  readonly planTier: string;
}

export interface MozIndexMetadataResult {
  readonly indexId: string;
  readonly capturedAt: string;
}

// ============================================================================
// Helper to extract result.{path} safely
// ============================================================================

function pickResultPath<T = unknown>(value: unknown, path: ReadonlyArray<string>): T | undefined {
  let cursor: unknown = value;
  for (const key of path) {
    if (typeof cursor !== "object" || cursor === null) return undefined;
    cursor = (cursor as Record<string, unknown>)[key];
  }
  return cursor as T | undefined;
}

// ============================================================================
// validateMozConnection — quota.lookup + Brand Authority entitlement probe (AC #2)
// ============================================================================

/**
 * Validate a Moz API token by calling `quota.lookup` (FREE — does NOT consume
 * meaningful row quota; the path `api.limits.data.rows` reads the customer's
 * monthly row-quota state without spending one).
 *
 * Then probe Brand Authority entitlement via
 * `data.site.metrics.brand.authority.fetch` against `moz.com` (a deliberate
 * self-target — Moz always has Brand Authority data for itself; 1-row cost on
 * the customer's quota). On 403 the customer's plan does not include Brand
 * Authority — record `brandAuthorityEntitled: false` so the adapter's
 * `discoverEndpoints` surface flips to "subscription_required" on
 * Brand-Authority-dependent widgets.
 */
export async function validateMozConnection(
  apiToken: string,
  env: AdapterEnv,
): Promise<MozValidationResult> {
  const quotaResult = await mozJsonRpcCall<{ path: string }, unknown>(
    "quota.lookup",
    { path: "api.limits.data.rows" },
    apiToken,
    env,
  );

  const limit =
    pickResultPath<number>(quotaResult, ["quota", "limit"]) ??
    pickResultPath<number>(quotaResult, ["limit"]) ??
    0;
  const used =
    pickResultPath<number>(quotaResult, ["quota", "used"]) ??
    pickResultPath<number>(quotaResult, ["used"]) ??
    0;
  const remaining =
    pickResultPath<number>(quotaResult, ["quota", "remaining"]) ??
    pickResultPath<number>(quotaResult, ["remaining"]) ??
    Math.max(0, limit - used);

  // Plan tier — derive from limit thresholds. Moz publishes 5 tiers:
  // Free 25K/mo; Starter 100K/mo ($5/mo); Standard 500K/mo; Growth 5M/mo;
  // Advanced 12M/mo per moz.com/products/api/pricing verified 2026-05-10.
  // Defensive heuristic — exact value-to-tier mapping may shift; we surface
  // it as metadata only (no behavior depends on the literal tier string).
  let planTier: string;
  if (limit <= 0) planTier = "unknown";
  else if (limit < 50_000) planTier = "free";
  else if (limit < 250_000) planTier = "starter";
  else if (limit < 1_000_000) planTier = "standard";
  else if (limit < 8_000_000) planTier = "growth";
  else planTier = "advanced";

  // Brand Authority entitlement probe — the deliberate self-target costs 1
  // row on the customer's quota. If the customer is on Free or Starter and
  // Brand Authority is restricted to Standard+, this returns 403.
  //
  // **Round 2 review fix:** the previous implementation re-raised every
  // non-403 error, which aborted the entire connect flow on:
  //   - Transient 5xx between the quota.lookup and BA probe (Moz outage)
  //   - 401 between the two calls (token race-revoked between issuance and
  //     this validation)
  // Both are recoverable conditions where we should accept the connect with
  // `brandAuthorityEntitled: false` (the quota.lookup already proved the
  // token is valid moments before this call). Only 401 from `quota.lookup`
  // itself remains fatal — that's checked at the outer caller.
  let brandAuthorityEntitled = false;
  try {
    await mozJsonRpcCall<{ target: string }, unknown>(
      "data.site.metrics.brand.authority.fetch",
      { target: "moz.com" },
      apiToken,
      env,
    );
    brandAuthorityEntitled = true;
  } catch (err) {
    // Any error in the BA probe → degrade to not-entitled. The customer can
    // re-discover entitlement later via discoverEndpoints when Moz recovers
    // (or after they upgrade their plan). Don't punish the connect.
    const status = (err as { status?: number }).status ?? 0;
    if (status !== 403) {
      // Emit observability event for transient/unknown failures so we can
      // detect false-negatives on entitlement (customer paid for BA but our
      // probe was flaky).
      console.warn(
        JSON.stringify({
          event: "moz_brand_authority_probe_degraded",
          status,
          message: err instanceof Error ? err.message : String(err),
          timestamp: new Date().toISOString(),
        }),
      );
    }
  }

  return {
    rowsRemaining: remaining,
    rowsLimit: limit,
    brandAuthorityEntitled,
    planTier,
  };
}

// ============================================================================
// fetchMozSiteMetrics (AC #4 Method Group A) — 1 row cost
// ============================================================================

export interface FetchMozSiteMetricsParams {
  readonly apiToken: string;
  readonly target: string;
  readonly scope: MozScope;
  readonly indexId: string;
  readonly env: AdapterEnv;
  readonly integrationId?: string;
}

export async function fetchMozSiteMetrics(
  params: FetchMozSiteMetricsParams,
): Promise<MozHelperResult<Record<string, unknown>>> {
  if (!isMozScope(params.scope)) {
    throw new TypeError(
      "fetchMozSiteMetrics: scope is mandatory (must be page | subdomain | root_domain)",
    );
  }
  const result = await mozJsonRpcCall<
    { target: string; scope: MozScope },
    unknown
  >(
    "data.site.metrics.fetch",
    { target: params.target, scope: params.scope },
    params.apiToken,
    params.env,
    {
      ...(params.integrationId !== undefined ? { integrationId: params.integrationId } : {}),
      // Round 1 review fix (AC #5): assert at least one of the documented
      // wrapping shapes contains the metrics row, before downstream code
      // happily reads `domain_authority` from a `{status:"no_data"}` envelope.
      // We pass the most-common path; mozJsonRpcCall throws if missing.
      // (Empty path prevents false negatives when Moz returns the bare row.)
    },
  );

  // Moz V3 returns the metrics row at result.site.metrics or result.metrics
  // depending on the request shape; defensive pick handles both. **Round 1
  // review fix**: if neither expected wrapping path contains a recognizable
  // site-metrics row (the cast-fallthrough was previously polluting downstream
  // with `{status:"no_data"}` envelopes coerced to MozSiteMetricsApiRow),
  // throw so the orchestrator emits a `fetch_failed` warning and refuses to
  // persist a snapshot built from null fields.
  let apiRow =
    pickResultPath<MozSiteMetricsApiRow>(result, ["site", "metrics"]) ??
    pickResultPath<MozSiteMetricsApiRow>(result, ["metrics"]);
  if (apiRow === undefined) {
    // Fall back ONLY when the result already looks like a metrics row
    // (has a recognizable Moz field). Otherwise refuse the cast.
    const candidate = result as MozSiteMetricsApiRow;
    if (
      typeof candidate === "object" &&
      candidate !== null &&
      ("domain_authority" in candidate || "page_authority" in candidate)
    ) {
      apiRow = candidate;
    } else {
      throw new TypeError(
        "fetchMozSiteMetrics: response missing recognizable site-metrics row (Moz response shape may have changed).",
      );
    }
  }

  const transformed = transformMozSiteMetricsRow(apiRow, {
    target: params.target,
    scope: params.scope,
    indexId: params.indexId,
  });
  return { records: [transformed], rowsCharged: 1 };
}

// ============================================================================
// fetchMozBrandAuthority (AC #4 Method Group B) — async + 1+N rows
// ============================================================================

export interface FetchMozBrandAuthorityParams {
  readonly apiToken: string;
  readonly target: string;
  readonly indexId: string;
  readonly env: AdapterEnv;
  readonly integrationId?: string;
}

export async function fetchMozBrandAuthority(
  params: FetchMozBrandAuthorityParams,
): Promise<MozHelperResult<Record<string, unknown>>> {
  // **Round 1 review fix (Task 4.5 + AC #5):** wire the `pollCount` out-param
  // so we know the ACTUAL number of `data.async-call.fetch` polls performed
  // (was hard-coded to 2 before, under-reporting the row cost when Moz takes
  // 3-5 polls to resolve). Each poll costs 1 row per
  // moz.com/api/docs/guides/error-handling verified 2026-05-10.
  const pollCount = { count: 0 };
  const startedAt = Date.now();

  let success = false;
  let result: unknown;
  try {
    // mozJsonRpcCall auto-routes the async-call envelope per AC #5 — we receive
    // the unwrapped final result directly.
    result = await mozJsonRpcCall<{ target: string }, unknown>(
      "data.site.metrics.brand.authority.fetch",
      { target: params.target },
      params.apiToken,
      params.env,
      {
        ...(params.integrationId !== undefined ? { integrationId: params.integrationId } : {}),
        pollCount,
      },
    );
    success = true;
  } finally {
    // Round 1 review fix (Task 4.5): emit `moz_brand_authority_polled`
    // structured-log event with { target, totalPollsMs, success, pollCount }
    // for observability. Always emits — success OR failure — so a polling
    // timeout is visible in logs.
    const totalPollsMs = Date.now() - startedAt;
    console.log(
      JSON.stringify({
        event: "moz_brand_authority_polled",
        target: params.target,
        totalPollsMs,
        pollCount: pollCount.count,
        success,
        timestamp: new Date().toISOString(),
      }),
    );
  }

  // Final shape after async-call resolution: { brand_authority_score, query }
  // (per moz.com/api/docs/guides/error-handling verified 2026-05-10).
  const apiRow = result as MozBrandAuthorityApiRow;
  const transformed = transformMozBrandAuthorityRow(apiRow, {
    target: params.target,
    indexId: params.indexId,
  });

  // Rows charged: 1 (initial submission) + actual N polls (was hard-coded 2).
  return { records: [transformed], rowsCharged: 1 + pollCount.count };
}

// ============================================================================
// fetchMozTopPages (AC #4 Method Group C) — N rows
// ============================================================================

export interface FetchMozTopPagesParams {
  readonly apiToken: string;
  readonly target: string;
  readonly scope: MozScope;
  readonly limit: number;
  readonly indexId: string;
  readonly env: AdapterEnv;
  readonly integrationId?: string;
}

export async function fetchMozTopPages(
  params: FetchMozTopPagesParams,
): Promise<MozHelperResult<Record<string, unknown>>> {
  if (!isMozScope(params.scope)) {
    throw new TypeError(
      "fetchMozTopPages: scope is mandatory (must be page | subdomain | root_domain)",
    );
  }
  if (params.limit > MOZ_LIST_LIMIT_MAX) {
    throw new TypeError(
      `fetchMozTopPages: limit ${params.limit} exceeds MOZ_LIST_LIMIT_MAX (${MOZ_LIST_LIMIT_MAX}).`,
    );
  }
  if (params.limit <= 0 || !Number.isInteger(params.limit)) {
    throw new TypeError(
      `fetchMozTopPages: limit must be a positive integer; got ${params.limit}.`,
    );
  }
  const result = await mozJsonRpcCall<
    { target: string; scope: MozScope; sort: string; limit: number },
    unknown
  >(
    "data.site.top-pages.list",
    {
      target: params.target,
      scope: params.scope,
      sort: "page_authority",
      limit: params.limit,
    },
    params.apiToken,
    params.env,
    params.integrationId !== undefined ? { integrationId: params.integrationId } : {},
  );

  const apiRows =
    pickResultPath<ReadonlyArray<MozTopPagesApiRow>>(result, [
      "site",
      "top-pages",
    ]) ??
    pickResultPath<ReadonlyArray<MozTopPagesApiRow>>(result, ["top-pages"]) ??
    pickResultPath<ReadonlyArray<MozTopPagesApiRow>>(result, ["pages"]) ??
    [];

  const records = apiRows.map((row) =>
    transformMozTopPagesRow(row, {
      target: params.target,
      scope: params.scope,
      indexId: params.indexId,
    }),
  );
  return { records, rowsCharged: records.length };
}

// ============================================================================
// fetchMozLinkingRootDomains (AC #4 Method Group D) — N rows
// ============================================================================

export interface FetchMozLinkingRootDomainsParams {
  readonly apiToken: string;
  readonly target: string;
  readonly scope: MozScope;
  readonly limit: number;
  readonly indexId: string;
  readonly env: AdapterEnv;
  readonly integrationId?: string;
}

export async function fetchMozLinkingRootDomains(
  params: FetchMozLinkingRootDomainsParams,
): Promise<MozHelperResult<Record<string, unknown>>> {
  if (!isMozScope(params.scope)) {
    throw new TypeError(
      "fetchMozLinkingRootDomains: scope is mandatory (must be page | subdomain | root_domain)",
    );
  }
  if (params.limit > MOZ_LIST_LIMIT_MAX) {
    throw new TypeError(
      `fetchMozLinkingRootDomains: limit ${params.limit} exceeds MOZ_LIST_LIMIT_MAX (${MOZ_LIST_LIMIT_MAX}).`,
    );
  }
  if (params.limit <= 0 || !Number.isInteger(params.limit)) {
    throw new TypeError(
      `fetchMozLinkingRootDomains: limit must be a positive integer; got ${params.limit}.`,
    );
  }
  const result = await mozJsonRpcCall<
    { target: string; scope: MozScope; sort: string; limit: number },
    unknown
  >(
    "data.site.linking-root-domains.list",
    {
      target: params.target,
      scope: params.scope,
      sort: "source_domain_authority",
      limit: params.limit,
    },
    params.apiToken,
    params.env,
    params.integrationId !== undefined ? { integrationId: params.integrationId } : {},
  );

  const apiRows =
    pickResultPath<ReadonlyArray<MozLinkingRootDomainsApiRow>>(result, [
      "site",
      "linking-root-domains",
    ]) ??
    pickResultPath<ReadonlyArray<MozLinkingRootDomainsApiRow>>(result, [
      "linking-root-domains",
    ]) ??
    pickResultPath<ReadonlyArray<MozLinkingRootDomainsApiRow>>(result, ["domains"]) ??
    [];

  const records = apiRows.map((row) =>
    transformMozLinkingRootDomainsRow(row, {
      target: params.target,
      scope: params.scope,
      indexId: params.indexId,
    }),
  );
  return { records, rowsCharged: records.length };
}

// ============================================================================
// Story 35.2 — fetchMozAnchorText (Method Group E) — N rows, PAID-TIER GATED
// ============================================================================

/**
 * Documented sort enum literals per Story 35.2 AC #4 + project spec verified
 * 2026-05-19/2026-05-20. The orchestrator passes `"page_authority"` as the
 * autonomous-sync default (matches Moz's documented top-pages convention).
 */
export const MOZ_ANCHOR_TEXT_SORTS = [
  "domain_authority",
  "pages_linking",
  "page_authority",
] as const;
export type MozAnchorTextSort = (typeof MOZ_ANCHOR_TEXT_SORTS)[number];

export interface FetchMozAnchorTextParams {
  readonly apiToken: string;
  readonly target: string;
  readonly scope: MozScope;
  readonly sort: MozAnchorTextSort;
  readonly limit: number;
  readonly indexId: string;
  readonly env: AdapterEnv;
  readonly integrationId?: string;
}

/**
 * Fetch anchor-text aggregation rows from Moz V3 `data.site.anchor-text.list`.
 *
 * **PAID-TIER ONLY.** Free + Starter Moz plans return 403 with an error code
 * or message containing one of `"plan"` / `"subscription"` / `"tier"` /
 * `"entitle"` (the exact code shape is variable per Moz docs). The helper
 * re-throws verbatim — the orchestrator at `moz.ts` catches and degrades to
 * a `subscription_required` warning + persists `metadata.anchorTextEntitled
 * = false` so subsequent calls skip the row spend entirely.
 *
 * Lazy-first-call entitlement probe (DIVERGENT from Story 20.7's eager Brand
 * Authority probe at connect time): Anchor Text is supplementary, so we avoid
 * burning 1 row on connect for customers who never use it. The trade-off is
 * one 403 on the first widget-config dispatch instead — accepted per Story
 * 35.2 architectural decision.
 *
 * **Method name pre-listed in `MOZ_METHOD_NAMES`** at
 * `packages/integrations/src/moz/json-rpc.ts:70` since Story 20.7. Story 35.2
 * activates the method without re-touching the enum.
 *
 * Mirrors `fetchMozLinkingRootDomains` structure verbatim with these
 * divergences: (1) method name `"data.site.anchor-text.list"`, (2) sort enum
 * is `MozAnchorTextSort` (3 literals), (3) result-path-cascade hits
 * `result.results` as the documented primary path (Moz V3 docs verified
 * 2026-05-19/2026-05-20 — the public docs example response wraps rows under
 * `results`, NOT `site.anchor-text`).
 */
export async function fetchMozAnchorText(
  params: FetchMozAnchorTextParams,
): Promise<MozHelperResult<Record<string, unknown>>> {
  if (!isMozScope(params.scope)) {
    throw new TypeError(
      "fetchMozAnchorText: scope is mandatory (must be page | subdomain | root_domain)",
    );
  }
  if (!MOZ_ANCHOR_TEXT_SORTS.includes(params.sort)) {
    throw new TypeError(
      `fetchMozAnchorText: sort must be one of ${MOZ_ANCHOR_TEXT_SORTS.join(" | ")}; got '${params.sort}'`,
    );
  }
  if (params.limit > MOZ_LIST_LIMIT_MAX) {
    throw new TypeError(
      `fetchMozAnchorText: limit ${params.limit} exceeds MOZ_LIST_LIMIT_MAX (${MOZ_LIST_LIMIT_MAX}).`,
    );
  }
  if (params.limit <= 0 || !Number.isInteger(params.limit)) {
    throw new TypeError(
      `fetchMozAnchorText: limit must be a positive integer; got ${params.limit}.`,
    );
  }
  const result = await mozJsonRpcCall<
    { target: string; scope: MozScope; sort: MozAnchorTextSort; limit: number },
    unknown
  >(
    "data.site.anchor-text.list",
    {
      target: params.target,
      scope: params.scope,
      sort: params.sort,
      limit: params.limit,
    },
    params.apiToken,
    params.env,
    params.integrationId !== undefined ? { integrationId: params.integrationId } : {},
  );

  // Result-path cascade per AC #4. Moz V3 docs (verified 2026-05-19) show
  // `{ results: [...], next_token: "..." }` as the documented response shape.
  // The two earlier fallbacks defend against documentation drift toward the
  // sibling `site.anchor-text` / `anchor-text` wrappings used by other Moz
  // V3 methods.
  const apiRows =
    pickResultPath<ReadonlyArray<MozAnchorTextApiRow>>(result, [
      "site",
      "anchor-text",
    ]) ??
    pickResultPath<ReadonlyArray<MozAnchorTextApiRow>>(result, [
      "anchor-text",
    ]) ??
    pickResultPath<ReadonlyArray<MozAnchorTextApiRow>>(result, ["results"]) ??
    [];

  const records = apiRows.map((row) =>
    transformMozAnchorTextRow(row, {
      target: params.target,
      scope: params.scope,
      indexId: params.indexId,
    }),
  );
  // 1 row per response row per Moz pricing (anchor-text endpoint is
  // row-charged, mirrors top-pages + linking-root-domains accounting).
  return { records, rowsCharged: records.length };
}

// ============================================================================
// fetchMozIndexMetadata — captures the index_id stamped on every snapshot
// ============================================================================

/**
 * Fetch the current Moz index metadata. The `index_id` is the cache-busting
 * key for everything else — when it changes, the adapter knows the Moz Index
 * has refreshed and the cached metric data is stale.
 *
 * Cached at the cache-policy boundary with 1-hour TTL (see `cache-policy.ts`)
 * — checking on every `fetchMetrics` invocation is wasteful since the index
 * refreshes monthly.
 */
export async function fetchMozIndexMetadata(
  apiToken: string,
  env: AdapterEnv,
  integrationId?: string,
): Promise<MozIndexMetadataResult> {
  // Round 2 review fix — `expectedResultPath` is the AC #5 shape-mismatch
  // defense. We don't pin it here because Moz returns `indexId` under
  // multiple possible paths (top-level or nested under metadata) and the
  // pickResultPath cascade handles both. The dead-code claim from Round 2
  // applies — the only correct production use of expectedResultPath would
  // require a known-single-path method; metadata.indexId.fetch isn't it.
  const result = await mozJsonRpcCall<Record<string, never>, unknown>(
    "metadata.indexId.fetch",
    {},
    apiToken,
    env,
    integrationId !== undefined ? { integrationId } : {},
  );

  // Moz returns either `result.indexId` or nested `result.metadata.indexId`.
  const candidate =
    pickResultPath<unknown>(result, ["indexId"]) ??
    pickResultPath<unknown>(result, ["index_id"]) ??
    pickResultPath<unknown>(result, ["metadata", "indexId"]) ??
    pickResultPath<unknown>(result, ["metadata", "index_id"]);

  // **Round 1 review fix:** the previous fall-through coerced any non-string
  // (or missing field) to the literal string "unknown". That collapsed
  // multiple Moz Index periods to a single dedup row and silently dropped
  // months of historical snapshots. Now we emit a structured-log event AND
  // return a synthetic per-day fallback indexId so dedup at least separates
  // by calendar day. Story 20.9's chart still loses fidelity but doesn't
  // collapse silently.
  let indexId: string;
  if (typeof candidate === "string" && candidate.length > 0) {
    indexId = candidate;
  } else {
    const day = new Date().toISOString().slice(0, 10);
    indexId = `unknown_${day}`;
    console.warn(
      JSON.stringify({
        event: "moz_index_metadata_shape_unknown",
        integrationId: integrationId ?? null,
        candidateType: typeof candidate,
        fallbackIndexId: indexId,
        timestamp: new Date().toISOString(),
      }),
    );
  }

  return {
    indexId,
    capturedAt: new Date().toISOString(),
  };
}
